/* יתרת העו"ש לאורך זמן: מודל אחד לכל המסכים.
   נקודת העוגן היא יתרת הפתיחה מההגדרות (סכום + תאריך, בתחילת או בסוף היום).
   היתרה בכל תאריך = העוגן, ועוד/פחות תנועות העו"ש שנסרקו ביניהם, ועוד תנועות שהתאריך שלהן
   עבר אבל עוד לא נסרקו (חיוב הכרטיס, חיובים קבועים).
   יתרות לפי תקופה נבנות לפי התקופה שאליה שויכה כל תנועה בסריקה, ולכן סגירה של תקופה
   היא תמיד בדיוק הפתיחה של התקופה הבאה. */
import { S, memo } from './state.js';
import { fISO, todayISO, iso10, absAmt, wD, dDays } from '../lib/format.js';
import { getTxSrc, isCardChargeRow, txPeriodOf } from './tx.js';
import { getKey, curKey, keyOfISO, periodStart, periodEnd } from './periods.js';
import { cardLateCharge, cardChargeAmount, cardFutureEstimate, cardTemplate } from './card.js';
import { recOpen } from './recurring.js';

export function hasBalance() {
  return !!S.set.balDate;
}

/** היתרה היום: העוגן, ועליו כל תנועות העו"ש שקרו מאז ועד היום.
    גם תנועה שהוחרגה מהסיכומים (סילוק כרטיס, העברה לחיסכון) הזיזה כסף בעו"ש באמת */
export function balToday() {
  return memo('balToday', () => {
    const base = Number(S.set.balAmount) || 0, from = S.set.balDate || '', eod = S.set.balEod, today = todayISO();
    let delta = 0, n = 0;
    if (from && from <= today) {
      S.tx.forEach((t) => {
        const ds = iso10(t.date);
        if (!ds || ds > today) return;
        if (eod ? ds <= from : ds < from) return;
        if (getTxSrc(t) === 'credit') return;
        const a = absAmt(t);
        if (t.type === 'income') { delta += a; n++; }
        else if (t.type === 'expense') { delta -= a; n++; }
      });
    }
    return { amount: Math.round((base + delta) * 100) / 100, base, delta: Math.round(delta * 100) / 100, count: n, from };
  });
}

/** תנועות שכבר קרו (התאריך עבר) ועוד לא נסרקו מהעו"ש */
export function lateItems() {
  return memo('lateItems', () => {
    const out = [];
    if (!S.set.balDate) return out;
    const lc = cardLateCharge();
    if (lc) out.push({ iso: lc.iso, name: 'חיוב כרטיס אשראי של ' + wD(lc.iso), amount: -lc.amount, card: true, est: !!lc.est });
    // יש חיוב כרטיס בתחזית (בפועל או הערכה): התבנית שלו לא נספרת שוב כחיוב קבוע
    const cardHas = !!cardChargeAmount() || cardFutureEstimate().amount > 0, tpl = cardTemplate();
    recOpen('cash').forEach((o) => {
      if (cardHas && tpl && o.r === tpl) return;
      out.push({ iso: o.iso, name: o.r.description, amount: (o.r.type === 'income' ? 1 : -1) * o.amount, recId: o.r.id, st: o.st });
    });
    return out.sort((a, b) => (a.iso < b.iso ? -1 : 1));
  });
}

/** היתרה של היום: מה שנסרק, ועוד מה שכבר קרה ועוד לא נסרק */
export function balEst() {
  return memo('balEst', () => {
    const b = balToday(), L = lateItems(), s = L.reduce((a, x) => a + x.amount, 0);
    return { known: b.amount, late: L, lateSum: s, amount: Math.round((b.amount + s) * 100) / 100, b };
  });
}

/** היום שבסופו היתרה ידועה */
export function bankAnchorEnd() {
  const { balDate, balEod } = S.set;
  return balEod ? balDate : fISO(new Date(new Date(balDate + 'T12:00:00').getTime() - 864e5));
}

const periodOf = (t) => txPeriodOf(t, getKey);

/** יתרת הפתיחה של תקופה k */
export function periodOpen(k) {
  if (!S.set.balDate) return null;
  return memo('periodOpen|' + k, () => {
    const base = Number(S.set.balAmount) || 0, aEnd = bankAnchorEnd();
    let mv = 0;
    S.tx.forEach((t) => {
      const ds = iso10(t.date);
      if (!ds || getTxSrc(t) === 'credit' || (t.type !== 'income' && t.type !== 'expense')) return;
      const a = (t.type === 'income' ? 1 : -1) * absAmt(t), m = periodOf(t);
      if (ds > aEnd) { if (m < k) mv += a; }
      else if (m >= k) mv -= a;
    });
    lateItems().forEach((x) => { if (x.iso > aEnd && keyOfISO(x.iso) < k) mv += x.amount; });
    return Math.round((base + mv) * 100) / 100;
  });
}

/** תנועות העו"ש ששייכות לתקופה k (עד תאריך upTo אם ניתן), עם חיוב הכרטיס בנפרד */
export function periodFlow(k, upTo) {
  let inc = 0, exp = 0, late = 0, n = 0, card = 0, ni = 0, ne = 0;
  const cards = [], txs = [];
  const aEnd = S.set.balDate ? bankAnchorEnd() : '';
  S.tx.forEach((t) => {
    const ds = iso10(t.date);
    if (!ds || getTxSrc(t) === 'credit' || (t.type !== 'income' && t.type !== 'expense')) return;
    if (periodOf(t) !== k || (upTo && ds > upTo)) return;
    const a = absAmt(t);
    n++;
    txs.push(t);
    if (isCardChargeRow(t)) { card += a; cards.push({ iso: ds, amount: a }); return; }
    if (t.type === 'income') { inc += a; ni++; } else { exp += a; ne++; }
  });
  lateItems().forEach((x) => { if (x.iso > aEnd && keyOfISO(x.iso) === k && (!upTo || x.iso <= upTo)) late += x.amount; });
  return { inc, exp, late, n, card, cards, ni, ne, txs };
}

export function periodOpening() {
  return Math.round(periodOpen(curKey()));
}

/** היתרה של היום מפורקת מפתיחת התקופה הנוכחית (= סגירת הקודמת).
    null כשהפירוק לא מסתכם ליתרה (תנועה ששויכה לתקופה עתידית), ואז מציגים מהעוגן */
export function balTodayParts() {
  if (!S.set.balDate) return null;
  const k = curKey(), ps = periodStart(k), today = todayISO();
  const open = periodOpen(k), E = balEst();
  if (open == null) return null;
  const F = periodFlow(k, today);
  const late = E.late.filter((x) => keyOfISO(x.iso) === k);
  const sum = open + F.inc - F.exp - F.card + late.reduce((s, x) => s + x.amount, 0);
  if (Math.abs(sum - E.amount) > 1) return null;
  return { ps, open, inc: F.inc, exp: F.exp, card: F.card, cards: F.cards, ni: F.ni, ne: F.ne, txs: F.txs, late, amount: E.amount };
}

/** תנועות העו"ש מאז העוגן, לפירוט היתרה */
export function balSinceAnchor() {
  const b = balToday(), today = todayISO(), from = b.from, eod = S.set.balEod;
  let inc = 0, exp = 0, ni = 0, ne = 0;
  const txs = [];
  S.tx.forEach((t) => {
    const ds = iso10(t.date);
    if (!ds || ds > today || !from) return;
    if (eod ? ds <= from : ds < from) return;
    if (getTxSrc(t) === 'credit') return;
    const a = absAmt(t);
    if (t.type === 'income') { inc += a; ni++; } else if (t.type === 'expense') { exp += a; ne++; } else return;
    txs.push(t);
  });
  return { b, inc, exp, ni, ne, txs };
}

/** האם יש נתונים ליתרה של תקופה k. אחורה מנקודת הפתיחה אפשר לחשב רק אם העו"ש נסרק כבר מתחילת התקופה.
    בלי זה, תקופות שלפני השימוש באפליקציה הציגו את יתרת הפתיחה כאילו זו הייתה היתרה שלהן */
export function periodBalKnown(k) {
  if (!S.set.balDate) return false;
  if (fISO(periodEnd(k)) >= S.set.balDate) return true;
  let first = '';
  S.tx.forEach((t) => {
    const ds = iso10(t.date);
    if (ds && getTxSrc(t) !== 'credit' && (!first || ds < first)) first = ds;
  });
  // עו"ש שנסרק עד 3 ימים אחרי תחילת התקופה: ימים בלי תנועות בתחילת תקופה הם דבר רגיל
  return !!first && first <= fISO(new Date(periodStart(k).getTime() + 3 * 864e5));
}

/** עד מתי היתרה מבוססת על נתונים אמיתיים: נקודת הפתיחה או תנועת העו"ש האחרונה שנסרקה.
    אחרי 10 ימים בלי נתונים, "בחשבון היום" הוא כבר הערכה בעיקר */
export function bankFreshness() {
  const today = todayISO();
  let last = S.set.balDate || '';
  S.tx.forEach((t) => {
    const ds = iso10(t.date);
    if (ds && ds <= today && getTxSrc(t) !== 'credit' && ds > last) last = ds;
  });
  const days = last ? dDays(today, last) : null;
  return { last, days, stale: days != null && days > 10 };
}
